'use client'

/**
 * AutoReloadDialog — Anthropic-style "Adjust auto-reload" modal.
 *
 * Two fields:
 *   - When credit balance reaches: <threshold $>
 *   - Bring balance back up to: <amount $>
 *
 * Footer: "Credits will be charged to <Brand> ending in <last4>" + Turn off + Update.
 *
 * Minimums (enforced server-side, mirrored here for fast feedback):
 *   - threshold >= $5
 *   - amount    >= $10
 *   - amount    > threshold  (otherwise the trigger would be a no-op)
 *
 * Stephen 2026-05-22 — credit balance pivot.
 */

import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { CurrencyInput } from './CurrencyInput'

interface PaymentMethod {
  id: string
  brand: string
  last4: string
}

interface AutoReloadState {
  enabled: boolean
  threshold_cents: number
  amount_cents: number
  has_payment_method: boolean
  payment_method: PaymentMethod | null
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  initial: AutoReloadState
  onChanged: () => void | Promise<void>
}

function formatBrand(brand: string): string {
  const lower = brand.toLowerCase()
  if (lower === 'amex') return 'Amex'
  return brand.charAt(0).toUpperCase() + brand.slice(1).toLowerCase()
}

export function AutoReloadDialog({ open, onOpenChange, initial, onChanged }: Props) {
  const [thresholdCents, setThresholdCents] = useState(initial.threshold_cents)
  const [amountCents, setAmountCents] = useState(initial.amount_cents)
  const [saving, setSaving] = useState(false)
  const [turningOff, setTurningOff] = useState(false)

  // Reset form when dialog opens or initial state changes.
  useEffect(() => {
    if (open) {
      setThresholdCents(initial.threshold_cents)
      setAmountCents(initial.amount_cents)
    }
  }, [open, initial.threshold_cents, initial.amount_cents])

  const errors = useMemo(() => {
    const e: { threshold?: string; amount?: string } = {}
    if (thresholdCents < 500) e.threshold = 'Minimum $5'
    if (amountCents < 1000) e.amount = 'Minimum $10'
    if (amountCents <= thresholdCents) {
      e.amount = 'Top-up amount must be greater than the threshold'
    }
    return e
  }, [thresholdCents, amountCents])

  const hasErrors = Object.keys(errors).length > 0

  const handleUpdate = async () => {
    if (hasErrors) return
    setSaving(true)
    try {
      const res = await fetch('/api/wallet/auto-fund', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          enabled: true,
          threshold_cents: thresholdCents,
          amount_cents: amountCents,
        }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({})) as { error?: string }
        throw new Error(data.error || `HTTP ${res.status}`)
      }
      toast.success(initial.enabled ? 'Auto-reload updated' : 'Auto-reload turned on')
      await onChanged()
      onOpenChange(false)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  const handleTurnOff = async () => {
    setTurningOff(true)
    try {
      const res = await fetch('/api/wallet/auto-fund', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: false }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({})) as { error?: string }
        throw new Error(data.error || `HTTP ${res.status}`)
      }
      toast.success('Auto-reload turned off')
      await onChanged()
      onOpenChange(false)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to turn off')
    } finally {
      setTurningOff(false)
    }
  }

  const pm = initial.payment_method

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" showCloseButton>
        <DialogHeader>
          <DialogTitle className="font-sans text-xl font-medium tracking-[-0.3px]">
            {initial.enabled ? 'Adjust auto-reload' : 'Set up auto-reload'}
          </DialogTitle>
          <DialogDescription>
            Avoid service disruptions by automatically topping up credits when your balance runs low.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ar-threshold">When credit balance reaches:</Label>
            <CurrencyInput
              id="ar-threshold"
              valueCents={thresholdCents}
              onChangeCents={setThresholdCents}
              minCents={500}
              disabled={saving || turningOff}
              error={errors.threshold}
            />
            {errors.threshold && (
              <p className="text-xs text-red-600">{errors.threshold}</p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ar-amount">Bring balance back up to:</Label>
            <CurrencyInput
              id="ar-amount"
              valueCents={amountCents}
              onChangeCents={setAmountCents}
              minCents={1000}
              disabled={saving || turningOff}
              error={errors.amount}
            />
            {errors.amount && (
              <p className="text-xs text-red-600">{errors.amount}</p>
            )}
          </div>
        </div>

        <DialogFooter className="mt-2 sm:items-center sm:justify-between">
          <p className="text-xs text-atrium-muted sm:mr-auto">
            {pm ? (
              <>
                Credits will be charged to {formatBrand(pm.brand)} ending in{' '}
                <span className="font-mono tabular-nums">{pm.last4}</span>
              </>
            ) : (
              <>No payment method on file. Add one from the credit balance card first.</>
            )}
          </p>
          <div className="flex items-center gap-2">
            {initial.enabled && (
              <Button
                type="button"
                variant="outline"
                onClick={handleTurnOff}
                disabled={turningOff || saving}
              >
                {turningOff ? 'Turning off…' : 'Turn off'}
              </Button>
            )}
            <Button
              type="button"
              variant="default"
              onClick={handleUpdate}
              disabled={!pm || hasErrors || saving || turningOff}
            >
              {saving ? 'Saving…' : initial.enabled ? 'Update' : 'Turn on'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
